// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// The event stream. This file is a CONTRACT, and the mechanism audit is its only serious consumer.
//
// ── WHY THE AUDIT NEEDS THIS AND NOT THE SIMULATION ─────────────────────────────────────────────
// A balance sim measures OUTCOMES — how good a rating a run reached, how long the tower survived. A
// broken mechanic does not produce a distinctive outcome; it produces a slightly worse one, which is
// indistinguishable from intended difficulty. A sibling game shipped with its turrets unable to shoot
// half the horde and sat comfortably inside 200 seeded difficulty runs, because half-dead guns just
// read as "wave 7 is hard". Here the equivalent is a dispatcher that sends every call to car 0: every
// wait gets longer, every rating gets worse, and the player's read is "adding cars doesn't help".
//
// So each event carries only OBSERVATIONS — where a car was, who got on, who was still standing there
// — and never a verdict. There is deliberately no `hadCallHere` flag on `pass`, no `wasCorrect` on
// `assign`, no `shouldHaveStopped` anywhere: a field computed by the suspect code and then checked
// against the suspect code is a tautology that passes on a completely broken game. The auditor in
// `tests/helpers/audit.ts` rebuilds the call ledger, the car-call set and each car's occupancy from
// `call`/`board`/`alight`/`giveup` alone, and every verdict is computed there.

export type Dir = -1 | 1;

export type SimEvent =
  // ── people ────────────────────────────────────────────────────────────────────────────────────
  /** A person appears, heading somewhere. */
  | { k: 'spawn'; t: number; a: number; unit: number; from: number; to: number }
  /** A person joins a landing queue and presses the button. */
  | { k: 'call'; t: number; a: number; riser: number; storey: number; dir: Dir }
  /** A person gets on. `to` is where they are going, which is how the auditor derives car calls. */
  | { k: 'board'; t: number; a: number; car: number; storey: number; dir: Dir; to: number }
  /** A person gets off. */
  | { k: 'alight'; t: number; a: number; car: number; storey: number }
  /** A person completes a journey. The satisfaction maths reads nothing else. */
  | { k: 'trip'; t: number; a: number; unit: number; from: number; to: number; waitTicks: number; rideTicks: number; walkTicks: number }
  /** A person waited past all patience and left the building. */
  | { k: 'giveup'; t: number; a: number; storey: number; waitTicks: number }
  /** A person took the stairs instead. */
  | { k: 'stairs'; t: number; a: number; from: number; to: number }
  /** A person is gone from the simulation. */
  | { k: 'despawn'; t: number; a: number }

  // ── cars ──────────────────────────────────────────────────────────────────────────────────────
  /** A car commits to a run. Emitted before it moves, so the auditor can order runs without floats. */
  | { k: 'move'; t: number; car: number; riser: number; from: number; to: number; dir: Dir }
  /** A car went by a storey without stopping. The single most important event in the file. */
  | { k: 'pass'; t: number; car: number; riser: number; storey: number; dir: Dir; occ: number; cap: number }
  /** A car stopped and opened its doors. `occ` is the load ON ARRIVAL, before anyone moves. */
  | { k: 'arrive'; t: number; car: number; riser: number; storey: number; dir: Dir; occ: number; cap: number }
  /** A car closed its doors. `leftBehind` is how many were still queued for its direction. */
  | { k: 'depart'; t: number; car: number; riser: number; storey: number; dir: Dir; occ: number; boarded: number; leftBehind: number }
  /** A car changed direction at the end of a sweep. */
  | { k: 'reverse'; t: number; car: number; storey: number; dir: Dir }
  /** A landing call was given to a car. `manual` marks a player summon. */
  | { k: 'assign'; t: number; riser: number; storey: number; dir: Dir; car: number; manual: boolean }
  /** The player tapped a crowd. A whiff asked for something already about to happen. */
  | { k: 'summon'; t: number; riser: number; storey: number; dir: Dir; whiff: boolean }
  /** A car could not commit to the stop it wanted because another car in the riser was in the way. */
  | { k: 'blocked'; t: number; car: number; riser: number; wanted: number }

  // ── the tower ─────────────────────────────────────────────────────────────────────────────────
  | { k: 'build'; t: number; what: string; storey: number; col: number; cost: number }
  | { k: 'sell'; t: number; what: string; id: number; refund: number }
  | { k: 'let'; t: number; unit: number; storey: number; pop: number }
  /** A tenancy has had enough and will be gone at the end of tomorrow. */
  | { k: 'notice'; t: number; unit: number; storey: number }
  | { k: 'leave'; t: number; unit: number; storey: number; pop: number }
  /** Rent collected from one unit at a night boundary, in pence, after every multiplier. */
  | { k: 'rent'; t: number; unit: number; pence: number }
  /** Footfall counted for one shop over the day just ended. */
  | { k: 'footfall'; t: number; unit: number; count: number }
  | { k: 'day'; t: number; day: number; cash: number; rating: number; pop: number };

export type EventKind = SimEvent['k'];

/** What the simulation calls when anything happens. Undefined in the shipped game's hot path? No —
 *  the HUD needs the same stream the audit does, so there is exactly one, and it is always on. */
export type EventSink = (e: SimEvent) => void;

/**
 * A bounded collector. The shipped game drains it every frame to drive the ticker and the sound; the
 * balance sim hands the simulation a sink that counts instead of stores, so a thousand runs never
 * hold a million objects.
 */
export function createLog(limit = 4096) {
  let buf: SimEvent[] = [];
  return {
    sink: ((e: SimEvent) => {
      if (buf.length < limit) buf.push(e);
    }) as EventSink,
    drain(): SimEvent[] {
      const out = buf;
      buf = [];
      return out;
    },
    peek(): readonly SimEvent[] {
      return buf;
    },
  };
}
